import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import type { LensCard, LensSection } from '~/types/leLens';

import { CANVAS_COLUMNS, resolveCanvasLayouts } from './canvasLayout';
import { expandAutomaticChartFrames } from './cards/cardReadingLayout';

export default function LayoutPreview({ cards, sections }: { cards: Array<LensCard>; sections: Array<LensSection> }) {
  const groups = [{ id: undefined, title: 'No section' }, ...sections].map((section) => ({
    ...section,
    cards: cards.filter((card) => card.section === section.id),
  }));

  return (
    <Stack aria-label="Layout preview" spacing={1} sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: 1.5 }}>
      <Typography color="text.secondary" variant="caption">
        Layout preview · not saved
      </Typography>
      {groups
        .filter((group) => group.cards.length > 0)
        .map((group) => {
          const resolved = resolveCanvasLayouts(group.cards, new Map());
          const frames = expandAutomaticChartFrames(
            resolved,
            resolved.map((card) => ({ ...card.layout!, i: card.id })),
            CANVAS_COLUMNS,
          );
          return (
            <Box key={group.id ?? 'ungrouped'}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>{group.title}</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${CANVAS_COLUMNS}, minmax(0, 1fr))`,
                  gridAutoRows: 4,
                }}
              >
                {frames.map((frame) => (
                  <Box
                    key={frame.i}
                    sx={{
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '8px',
                      gridColumn: `${frame.x + 1} / span ${frame.w}`,
                      gridRow: `${frame.y + 1} / span ${frame.h}`,
                      m: '1px',
                      minWidth: 0,
                      overflow: 'hidden',
                      px: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: 10, lineHeight: 1.2, textAlign: 'center' }}>
                      {group.cards.find((card) => card.id === frame.i)?.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
    </Stack>
  );
}
